/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { AlertBanner, ConfirmationDialog } from '@opengeoweb/shared';
import { useMutateDeleteDrawing } from '../../api/warning-api/hooks';
import { useWarningsTranslation } from '../../utils/i18n';

export const getDeleteDescription = (
  objectName: string,
  t: TFunction,
): string => `${t('delete-dialog-description')} "${objectName}"?`;

export const getDeleteSucces = (objectName: string, t: TFunction): string =>
  `Object "${objectName}" ${t('delete-succes')}`;

export const ConfirmDeleteDialog: React.FC<{
  objectId: string;
  objectName: string;
  onDeleteSucces: (objectId: string) => void;
  onClose: () => void;
}> = ({
  objectId,
  objectName,
  onDeleteSucces = (): void => {},
  onClose = (): void => {},
}) => {
  const { t } = useWarningsTranslation();

  const { mutate: deleteDrawing, isPending, error } = useMutateDeleteDrawing();

  const onSubmit = (): void => {
    deleteDrawing(
      { drawingId: objectId, objectName },
      {
        onSuccess: () => {
          onDeleteSucces(objectId);
        },
      },
    );
  };

  const description = getDeleteDescription(objectName, t);

  return (
    <ConfirmationDialog
      cancelLabel={t('delete-dialog-cancel')}
      confirmLabel={t('delete-dialog-confirm')}
      description={!error ? description : ''}
      isLoading={isPending}
      onClose={onClose}
      onSubmit={onSubmit}
      open
      title={t('delete-dialog-title')}
      {...(error?.message && {
        content: (
          <Box sx={{ marginBottom: 0 }}>
            <AlertBanner title={error.message} />
            <Typography sx={{ marginTop: 1 }} variant="body1">
              {description}
            </Typography>
          </Box>
        ),
      })}
    />
  );
};
